import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  isOrgGatedExternalStorageProvider,
  kExternalStorageProvider,
  kExternalStorageProviderDisplayName,
  kIsExternalStorageProvider,
  kVisibleExternalStorageProviders,
  type ExternalStorageProvider,
} from '@/constants/constants-external-storage'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { getAnalyticsApiErrorProps } from '@/analytics/utils/analytics-helpers'
import { getDriveSyncOrganizationId, trackMatterDriveSyncEvent } from '@/analytics/utils/matter-drive-sync-analytics'
import { AuthContext } from '@/context/auth-context'
import getBoxAuthorizeUrl from '@/settings/fetch/get-box-authorize-url'
import getClioAuthorizeUrl from '@/settings/fetch/get-clio-authorize-url'
import getDropboxAuthorizeUrl, { DropboxAccountRole } from '@/settings/fetch/get-dropbox-authorize-url'
import getGoogleDriveAuthorizeUrl from '@/settings/fetch/get-google-drive-authorize-url'
import getOneDriveAuthorizeUrl from '@/settings/fetch/get-onedrive-authorize-url'
import getSharePointAuthorizeUrl from '@/settings/fetch/get-sharepoint-authorize-url'
import deleteExternalStorageAccount from '@/settings/fetch/delete-external-storage-account'
import postExternalStorageOAuthComplete from '@/settings/fetch/post-external-storage-oauth-complete'
import type { ExternalStorageDisconnectFileDisposition } from '@/settings/schemas/external-storage-schema'
import { pickVisibleAccount } from '@/settings/utils/pick-visible-account'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import {
  ExternalStorageActions,
  kExternalStorageRequestStatus,
  selectConnectedAccounts,
  selectExternalStorageAccountsRequest,
} from '@/user_files/external-storage/store/slice'
import useClioOrgAccess from '@/user_files/external-storage/hooks/useClioOrgAccess'

const PENDING_PROVIDER_STORAGE_KEY = 'paxton:dms-pending-provider'
const OAUTH_HANDOFF_PARAM = 'external_storage_handoff'
const OAUTH_HANDOFF_PROVIDER_PARAM = 'provider'

/**
 * Reads the one-time handoff id the OAuth callback added to the Settings URL and removes it from
 * the address bar, so a reload or a shared link cannot replay it.
 */
function takeOAuthHandoffFromUrl(): { handoffId: string; provider: ExternalStorageProvider } | null {
  const url = new URL(window.location.href)
  const handoffId = url.searchParams.get(OAUTH_HANDOFF_PARAM)
  if (!handoffId) return null

  const provider = url.searchParams.get(OAUTH_HANDOFF_PROVIDER_PARAM)
  url.searchParams.delete(OAUTH_HANDOFF_PARAM)
  url.searchParams.delete(OAUTH_HANDOFF_PROVIDER_PARAM)
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)

  if (!kIsExternalStorageProvider(provider)) return null
  return { handoffId, provider }
}

const PROVIDER_AUTHORIZE_FETCHER: Record<ExternalStorageProvider, () => Promise<{ authorization_url: string }>> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: getGoogleDriveAuthorizeUrl,
  [kExternalStorageProvider.DROPBOX]: getDropboxAuthorizeUrl,
  [kExternalStorageProvider.BOX]: getBoxAuthorizeUrl,
  [kExternalStorageProvider.ONEDRIVE]: getOneDriveAuthorizeUrl,
  [kExternalStorageProvider.CLIO]: getClioAuthorizeUrl,
  [kExternalStorageProvider.SHAREPOINT]: getSharePointAuthorizeUrl,
}

async function getAuthorizeUrl(provider: ExternalStorageProvider, dropboxAccountRole?: DropboxAccountRole): Promise<string> {
  if (provider === kExternalStorageProvider.DROPBOX) {
    if (!dropboxAccountRole) throw new Error('Choose a Dropbox account type before continuing.')
    const { authorization_url } = await getDropboxAuthorizeUrl(dropboxAccountRole)
    return authorization_url
  }

  const { authorization_url } = await PROVIDER_AUTHORIZE_FETCHER[provider]()
  return authorization_url
}

export default function useConnectedAccounts() {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const { userAccountData } = useContext(AuthContext)
  const organizationId = getDriveSyncOrganizationId(userAccountData)
  const accounts = useAppSelector(selectConnectedAccounts)
  const accountsRequest = useAppSelector(selectExternalStorageAccountsRequest)
  const [confirmingProvider, setConfirmingProvider] = useState<ExternalStorageProvider | null>(null)
  const [loadingProvider, setLoadingProvider] = useState<ExternalStorageProvider | null>(null)
  const [pendingProviderToastShown, setPendingProviderToastShown] = useState(false)
  const oauthHandoffHandledRef = useRef(false)

  // Clio is gated to allowlisted organizations. A provider that survives this
  // filter is treated as connectable by ConnectedAccounts.
  const clioOrgAccess = useClioOrgAccess()
  const orgGatedAccess: Record<string, boolean> = useMemo(() => ({ [kExternalStorageProvider.CLIO]: clioOrgAccess }), [clioOrgAccess])
  const providers = useMemo(
    () => kVisibleExternalStorageProviders.filter((provider) => !isOrgGatedExternalStorageProvider(provider) || orgGatedAccess[provider]),
    [orgGatedAccess]
  )

  const visibleAccount = useMemo(() => pickVisibleAccount(accounts), [accounts])
  const accountsLoadError = accountsRequest.status === kExternalStorageRequestStatus.ERROR
  const isInitialLoading = accountsRequest.status === kExternalStorageRequestStatus.IDLE || accountsRequest.status === kExternalStorageRequestStatus.LOADING

  const showToast = (type: GlobalToastType, message: string) => {
    dispatch(openGlobalToast({ type, message, durationMs: kToastDurationShortMs }))
  }

  useEffect(() => {
    if (accountsRequest.status === kExternalStorageRequestStatus.IDLE) {
      dispatch(ExternalStorageActions.fetchConnectedAccounts())
    }
  }, [accountsRequest.status, dispatch])

  // Finish a connection the OAuth callback handed back. The ref keeps StrictMode's repeated mount
  // effect from posting the single-use handoff twice.
  useEffect(() => {
    if (oauthHandoffHandledRef.current) return
    oauthHandoffHandledRef.current = true

    const handoff = takeOAuthHandoffFromUrl()
    if (!handoff) return

    postExternalStorageOAuthComplete(handoff.provider, handoff.handoffId)
      .then(() => dispatch(ExternalStorageActions.fetchConnectedAccounts({ force: true })))
      .catch(() => {
        window.sessionStorage.removeItem(PENDING_PROVIDER_STORAGE_KEY)
        showToast(GlobalToastType.ERROR, `${kExternalStorageProviderDisplayName[handoff.provider]} failed to connect. Please try again.`)
      })
    // Runs once per mount: the handoff lives only in the URL the callback redirected to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (accountsRequest.status !== kExternalStorageRequestStatus.SUCCESS || pendingProviderToastShown) return

    const pendingProvider = window.sessionStorage.getItem(PENDING_PROVIDER_STORAGE_KEY)
    if (!kIsExternalStorageProvider(pendingProvider)) {
      if (pendingProvider) window.sessionStorage.removeItem(PENDING_PROVIDER_STORAGE_KEY)
      return
    }

    if (accounts.some((account) => account.provider === pendingProvider && account.status === 'connected')) {
      window.sessionStorage.removeItem(PENDING_PROVIDER_STORAGE_KEY)
      showToast(GlobalToastType.SUCCESS, `${kExternalStorageProviderDisplayName[pendingProvider]} is now connected`)
      trackMatterDriveSyncEvent(AnalyticsEvent.MatterDriveSyncAccountConnected, { externalStorageProvider: pendingProvider, organizationId }, trackEvent)
      setPendingProviderToastShown(true)
    }
    // showToast/trackEvent are recreated every render, so keep this effect keyed to the stored
    // account transition only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, accountsRequest.status, pendingProviderToastShown])

  const handleProviderConnect = (provider: ExternalStorageProvider) => {
    setConfirmingProvider(provider)
    trackMatterDriveSyncEvent(AnalyticsEvent.MatterDriveSyncAccountConnectModalOpened, { externalStorageProvider: provider, organizationId }, trackEvent)
  }

  const handleConfirmConnect = async (dropboxAccountRole?: DropboxAccountRole) => {
    if (!confirmingProvider) return

    setLoadingProvider(confirmingProvider)
    trackMatterDriveSyncEvent(AnalyticsEvent.MatterDriveSyncConnectRedirectStarted, { externalStorageProvider: confirmingProvider, organizationId }, trackEvent)
    try {
      const authorization_url = await getAuthorizeUrl(confirmingProvider, dropboxAccountRole)
      window.sessionStorage.setItem(PENDING_PROVIDER_STORAGE_KEY, confirmingProvider)
      window.location.href = authorization_url
    } catch (error) {
      const providerLabel = kExternalStorageProviderDisplayName[confirmingProvider]
      window.sessionStorage.removeItem(PENDING_PROVIDER_STORAGE_KEY)
      showToast(GlobalToastType.ERROR, `${providerLabel} failed to connect. Please try again.`)
      trackMatterDriveSyncEvent(
        AnalyticsEvent.MatterDriveSyncConnectRedirectFailed,
        { externalStorageProvider: confirmingProvider, organizationId, ...getAnalyticsApiErrorProps(error) },
        trackEvent
      )
      setLoadingProvider(null)
      setConfirmingProvider(null)
    }
  }

  const handleDisconnect = async ({ fileDisposition }: { fileDisposition: ExternalStorageDisconnectFileDisposition }) => {
    if (!visibleAccount) return false

    setLoadingProvider(visibleAccount.provider)
    // TODO(FOUND-151): Add the selected disposition and advisory preview counts after the
    // disconnect analytics contract and rollout are reviewed.
    try {
      const updated = await deleteExternalStorageAccount(visibleAccount.id, fileDisposition)
      dispatch(ExternalStorageActions.accountDisconnected({ account: updated }))
      trackMatterDriveSyncEvent(
        AnalyticsEvent.MatterDriveSyncAccountDisconnected,
        {
          externalStorageProvider: visibleAccount.provider,
          organizationId,
        },
        trackEvent
      )
      return true
    } catch (error) {
      trackMatterDriveSyncEvent(
        AnalyticsEvent.MatterDriveSyncAccountDisconnectFailed,
        {
          externalStorageProvider: visibleAccount.provider,
          organizationId,
          ...getAnalyticsApiErrorProps(error),
        },
        trackEvent
      )
      return false
    } finally {
      setLoadingProvider(null)
    }
  }

  const handleReconnect = () => {
    if (!visibleAccount) return

    setConfirmingProvider(visibleAccount.provider)
    trackMatterDriveSyncEvent(
      AnalyticsEvent.MatterDriveSyncAccountConnectModalOpened,
      { externalStorageProvider: visibleAccount.provider, organizationId },
      trackEvent
    )
  }

  return {
    accountsLoadError,
    confirmingProvider,
    handleConfirmConnect,
    handleDisconnect,
    handleProviderConnect,
    handleReconnect,
    isInitialLoading,
    loadingProvider,
    providers,
    setConfirmingProvider,
    visibleAccount,
  }
}
